/**
 * What a request covers: which kinds of message, on which channels, from which
 * businesses — every business, or chosen clients and business types — minus
 * those left out, plus those let through.
 *
 * Empty means "all" for each list, as the server reads it. The server checks
 * the combination (nothing both covered and left out) and says what is wrong.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Badge, Checkbox, RecordSelect, Select } from '@connect/web-shared';

import {
  CHANNEL_LABEL,
  requestKeys,
  requestsApi,
  type Category,
  type Channel,
  type Named,
  type ScopeIn,
} from './requests-api';

type ClientList = 'clients' | 'except_clients' | 'allow_clients';
type ClassList = 'classifications' | 'except_classifications' | 'allow_classifications';

/** The chosen clients' and groups' names, kept beside their ids for the chips. */
export type Names = Record<string, string>;

const CHANNELS: Channel[] = ['sms', 'whatsapp', 'rcs', 'email', 'webchat', 'voice'];

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function ScopeEditor({
  value,
  onChange,
  names,
  onNames,
  categories,
  allows,
}: {
  value: ScopeIn;
  onChange: (next: ScopeIn) => void;
  names: Names;
  onNames: (next: Names) => void;
  categories: Category[];
  /** A stop may let chosen businesses through; an exception names them as its scope. */
  allows: boolean;
}) {
  const classes = useQuery({ queryKey: requestKeys.classifications, queryFn: requestsApi.classifications });
  const remember = (id: string, name: string) => onNames({ ...names, [id]: name });

  const clientPicker = (list: ClientList, label: string, hint: string) => (
    <Chosen
      label={label}
      hint={hint}
      chosen={value[list].map((id) => ({ public_id: id, name: names[id] ?? 'A client' }))}
      onRemove={(id) => onChange({ ...value, [list]: value[list].filter((v) => v !== id) })}
    >
      <RecordSelect
        endpoint="/crm/clients"
        value={null}
        onChange={(id, option) => {
          if (!id || value[list].includes(id)) return;
          remember(id, option?.label ?? 'A client');
          onChange({ ...value, [list]: [...value[list], id] });
        }}
        toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
        placeholder="Add a client…"
      />
    </Chosen>
  );

  const classPicker = (list: ClassList, label: string) => {
    const available = (classes.data ?? []).filter((c) => !value[list].includes(c.public_id));
    return (
      <Chosen
        label={label}
        chosen={value[list].map((id) => ({
          public_id: id,
          name: names[id] ?? classes.data?.find((c) => c.public_id === id)?.name ?? 'A group',
        }))}
        onRemove={(id) => onChange({ ...value, [list]: value[list].filter((v) => v !== id) })}
      >
        <Select
          aria-label={label}
          value=""
          placeholder={available.length ? 'Add a business type or group…' : 'None to add'}
          disabled={!available.length}
          options={available.map((c) => ({
            value: c.public_id,
            label: `${c.name} · ${c.kind === 'business_type' ? 'business type' : 'group'}`,
          }))}
          onChange={(event) => {
            const id = event.target.value;
            const found = classes.data?.find((c) => c.public_id === id);
            if (!found) return;
            remember(id, found.name);
            onChange({ ...value, [list]: [...value[list], id] });
          }}
        />
      </Chosen>
    );
  };

  return (
    <div className="space-y-4">
      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-body">Kinds of message</legend>
        <p className="text-2xs text-faint">None ticked: every kind.</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {categories.map((category) => (
            <Checkbox
              key={category.key}
              label={category.label}
              checked={value.categories.includes(category.key)}
              onChange={() => onChange({ ...value, categories: toggle(value.categories, category.key) })}
            />
          ))}
        </div>
      </fieldset>
      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-body">Channels</legend>
        <p className="text-2xs text-faint">None ticked: every channel.</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {CHANNELS.map((channel) => (
            <Checkbox
              key={channel}
              label={CHANNEL_LABEL[channel]}
              checked={value.channels.includes(channel)}
              onChange={() => onChange({ ...value, channels: toggle(value.channels, channel) })}
            />
          ))}
        </div>
      </fieldset>
      <div className="grid gap-4 md:grid-cols-2">
        {clientPicker('clients', 'Businesses covered', 'None chosen: every business.')}
        {classPicker('classifications', 'Business types and groups covered')}
        {clientPicker('except_clients', 'Businesses left out', 'Never covered, whatever else says so.')}
        {classPicker('except_classifications', 'Business types and groups left out')}
        {allows ? (
          <>
            {clientPicker('allow_clients', 'Businesses let through', 'Their messages still reach the person.')}
            {classPicker('allow_classifications', 'Business types and groups let through')}
          </>
        ) : null}
      </div>
    </div>
  );
}

function Chosen({
  label,
  hint,
  chosen,
  onRemove,
  children,
}: {
  label: string;
  hint?: string;
  chosen: Named[];
  onRemove: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-body">{label}</p>
      {hint ? <p className="text-2xs text-faint">{hint}</p> : null}
      {chosen.length ? (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((item) => (
            <Badge key={item.public_id} tone="neutral">
              <span className="flex items-center gap-1">
                {item.name}
                <button
                  type="button"
                  className="text-faint hover:text-heading"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => onRemove(item.public_id)}
                >
                  <X className="size-3" />
                </button>
              </span>
            </Badge>
          ))}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** A scope as words: "Promotional · WhatsApp · Sunrise Bank, except Lotus Clinic". */
export function describeScope(
  scope: {
    categories: string[];
    channels: string[];
    clients: Named[];
    classifications: Named[];
    except_clients: Named[];
    except_classifications: Named[];
    allow_clients: Named[];
    allow_classifications: Named[];
  },
  categories: Category[],
): string[] {
  const label = (key: string) => categories.find((c) => c.key === key)?.label ?? key;
  const names = (items: Named[]) => items.map((i) => i.name).join(', ');
  const lines = [
    scope.categories.length ? scope.categories.map(label).join(', ') : 'Every kind of message',
    scope.channels.length
      ? scope.channels.map((c) => CHANNEL_LABEL[c as Channel] ?? c).join(', ')
      : 'Every channel',
    scope.clients.length || scope.classifications.length
      ? [names(scope.clients), names(scope.classifications)].filter(Boolean).join(' · ')
      : 'Every business',
  ];
  const left = [names(scope.except_clients), names(scope.except_classifications)].filter(Boolean);
  if (left.length) lines.push(`Except ${left.join(', ')}`);
  const through = [names(scope.allow_clients), names(scope.allow_classifications)].filter(Boolean);
  if (through.length) lines.push(`Let through: ${through.join(', ')}`);
  return lines;
}

/** For a dialog that keeps its own copy while it is open. */
export function useScopeState(initial: ScopeIn) {
  const [scope, setScope] = useState<ScopeIn>(initial);
  const [names, setNames] = useState<Names>({});
  return { scope, setScope, names, setNames };
}
